/**
 * PublicReglements.tsx
 * Route : /reglements
 * Recherche sémantique dans les règlements européens (reglement_eu_qwen3)
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { getDateLocale } from '@/i18n';
import { PublicHeader, PublicFooter, MCPBadge } from '@/components/jurisprudence';
import { SmartSearchBar } from '@/components/jurisprudence/SmartSearch';
import { SearchModeToggle, useSearchMode, type SearchMode } from '@/components/jurisprudence/SearchModeToggle';

const SITE_URL    = 'https://smartlawyer.ai';
const DISPLAY_STEP = 10;

// Règlements EU emblématiques pour les suggestions rapides
// labelKey : libellé affiché (i18n) ; query : requête envoyée à l'API (inchangée)
const _QUICK_EXAMPLES = [
  { labelKey: 'reglements.examples.gdpr_consent', query: 'consentement traitement données personnelles' },
  { labelKey: 'reglements.examples.ai_act_obligations', query: 'obligations systèmes intelligence artificielle haut risque' },
  { labelKey: 'reglements.examples.dsa_liability', query: 'responsabilité plateformes contenus illicites' },
  { labelKey: 'reglements.examples.dma_gatekeepers', query: 'obligations contrôleurs d accès gatekeepers' },
  { labelKey: 'reglements.examples.eidas_signatures', query: 'signature électronique qualifiée reconnaissance' },
];

// labelKey (optionnel) : libellé traduit ; sinon `label` (acronyme identique dans toutes les langues)
const CELEX_FILTERS: { celex: string; label: string; labelKey?: string; slug: string }[] = [
  { celex: '32016R0679', label: 'RGPD', labelKey: 'reglements.gdpr', slug: 'reglement-ue-2016-679' },
  { celex: '32024R1689', label: 'AI Act',         slug: 'reglement-ue-2024-1689' },
  { celex: '32022R2065', label: 'DSA',            slug: 'reglement-ue-2022-2065' },
  { celex: '32022R1925', label: 'DMA',            slug: 'reglement-ue-2022-1925' },
  { celex: '32014R0910', label: 'eIDAS',          slug: 'reglement-ue-2014-910' },
  { celex: '32023R1114', label: 'MiCA',           slug: 'reglement-ue-2023-1114' },
];

interface RegResult {
  id: string;
  score: number;
  celex: string;
  slug_code: string;
  slug: string;
  numero: string;
  code: string;
  texte: string;
  section_summary: string;
}

function RegCard({ r }: { r: RegResult }) {
  const { t } = useTranslation();
  const numero   = r.numero || '?';
  const codeName = r.code || r.celex || '';
  const excerpt  = r.section_summary || r.texte || '';
  const path     = r.slug_code && r.numero
    ? `/reglements/${r.slug_code}/${encodeURIComponent(r.numero)}`
    : null;

  return (
    <div className="rounded-xl border border-border bg-card hover:border-primary/30 hover:shadow-sm transition-all p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-600 dark:text-indigo-400">
              🇪🇺 Art. {numero}
            </span>
            {r.celex && (
              <span className="text-[10px] text-muted-foreground font-mono">{r.celex}</span>
            )}
          </div>
          <p className="text-[13px] font-semibold text-foreground leading-snug line-clamp-2 mb-1">
            {codeName}
          </p>
          {excerpt && (
            <p className="text-[12px] text-muted-foreground leading-relaxed line-clamp-3">
              {excerpt}
            </p>
          )}
        </div>
        <div className="flex-shrink-0 text-[10px] text-muted-foreground/60 font-mono">
          {Math.round(r.score * 100)}%
        </div>
      </div>
      {path && (
        <div className="mt-3 pt-3 border-t border-border/50">
          <Link
            to={path}
            className="text-[12px] text-primary font-medium hover:underline"
          >
            {t('articles.read_article')}
          </Link>
        </div>
      )}
    </div>
  );
}

interface CorpusStats {
  reglements_eu_count: number;
  reglements_eu_reglements: number;
}

export default function PublicReglements() {
  const { t } = useTranslation();
  const locale = getDateLocale();
  const [query, setQuery]           = useState('');
  const [searching, setSearching]   = useState(false);
  const [allResults, setAllResults] = useState<RegResult[]>([]);
  const [results, setResults]       = useState<RegResult[]>([]);
  const [displayCount, setDisplayCount] = useState(0);
  const [searchDone, setSearchDone] = useState(false);
  const [searchTime, setSearchTime] = useState<number | null>(null);
  const [activeCelex, setActiveCelex] = useState<string>('');
  const [corpusStats, setCorpusStats] = useState<CorpusStats | null>(null);
  const [searchMode, setSearchMode]   = useState<SearchMode>('libre');
  const toggleMode = useSearchMode(searchMode, setSearchMode, 'libre');
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch('/api/fiches/corpus-stats')
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setCorpusStats(d))
      .catch(() => null);
  }, []);

  const doSearch = useCallback(async (q: string, celex?: string) => {
    if (!q.trim()) return;
    setSearching(true);
    setSearchDone(false);
    setAllResults([]);
    setResults([]);
    setDisplayCount(0);
    const t0 = performance.now();
    try {
      const body: Record<string, unknown> = { query: q, limit: 60 };
      if (celex) body.celex = celex;

      const res  = await fetch('/api/reglements/search', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });
      const data = await res.json();
      const fetched: RegResult[] = data.results || [];
      setAllResults(fetched);
      setResults(fetched.slice(0, DISPLAY_STEP));
      setDisplayCount(DISPLAY_STEP);
      setSearchTime(Math.round(performance.now() - t0));
      setSearchDone(true);
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    } catch (e) {
      console.error('reglements search error:', e);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    doSearch(query, activeCelex || undefined);
  };

  const _handleExample = (q: string) => {
    setQuery(q);
    doSearch(q, activeCelex || undefined);
  };

  const handleCelexFilter = (celex: string) => {
    const next = activeCelex === celex ? '' : celex;
    setActiveCelex(next);
    if (query.trim()) doSearch(query, next || undefined);
  };

  const handleGuidedSearch = useCallback((q: string, filters: Record<string, string>) => {
    const celex = filters.celex ?? '';
    setQuery(q);
    setActiveCelex(celex);
    doSearch(q, celex || undefined);
  }, [doSearch]);

  const handleReset = () => {
    setQuery('');
    setAllResults([]);
    setResults([]);
    setSearchDone(false);
    setSearchTime(null);
    setDisplayCount(0);
    setActiveCelex('');
  };

  const loadMore = () => {
    setDisplayCount(prev => {
      const next = Math.min(prev + DISPLAY_STEP, allResults.length);
      setResults(allResults.slice(0, next));
      return next;
    });
  };

  return (
    <>
      <Helmet>
        <title>{t('reglements.meta_title')}</title>
        <meta name="description" content={t('reglements.meta_description')} />
        <link rel="canonical" href={`${SITE_URL}/reglements`} />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <PublicHeader />

        <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-10">

          {/* Breadcrumb */}
          <nav className="text-xs text-muted-foreground mb-6 flex items-center gap-1.5">
            <Link to="/" className="hover:text-foreground transition-colors">{t('articles.home')}</Link>
            <span className="text-muted-foreground/40">/</span>
            <span className="text-foreground">{t('reglements.breadcrumb')}</span>
          </nav>

          {/* ── En-tête épuré ── */}
          <div className="mb-6">
            <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="text-3xl">📋</span>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold leading-tight"><Trans t={t} i18nKey="reglements.heading" components={{ em: <em className="not-italic text-primary" /> }} /></h1>
                </div>
              </div>
              <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
                <Link to="/jurisprudence/rgpd" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">🔒 {t('reglements.corpus_rgpd')}</Link>
                <span className="text-border">·</span>
                <Link to="/jurisprudence/cjue" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">🇪🇺 {t('reglements.corpus_cjue')}</Link>
                <span className="text-border">·</span>
                <Link to="/articles" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">⚖️ {t('articles.title')}</Link>
              </div>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/50">
                <span className="text-base font-bold text-foreground tabular-nums">
                  {corpusStats ? corpusStats.reglements_eu_count.toLocaleString(locale) : <span className="inline-block w-10 h-4 bg-indigo-100 rounded animate-pulse" />}
                </span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('reglements.stat_articles')}</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-violet-50 dark:bg-violet-900/10 border border-violet-100 dark:border-violet-800/40">
                <span className="text-base font-bold text-violet-700 dark:text-violet-400 tabular-nums">
                  {corpusStats ? corpusStats.reglements_eu_reglements.toLocaleString(locale) : <span className="inline-block w-10 h-4 bg-violet-100 rounded animate-pulse" />}
                </span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('reglements.stat_regulations')}</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/60 border border-border">
                <span className="text-base font-bold text-foreground">2000+</span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('reglements.stat_since')}</span>
              </div>
            </div>
          </div>

          {/* ── Zone de recherche unifiée ── */}
          <div className="rounded-2xl border border-border bg-muted/40 px-5 sm:px-6 pt-4 pb-4 mb-6">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="text-[13px] font-bold text-foreground">
                <span className="text-lg leading-none">📋</span>{' '}
                {corpusStats?.reglements_eu_count
                  ? <Trans t={t} i18nKey="reglements.indexed_articles" components={{ strong: <strong className="text-primary">{corpusStats.reglements_eu_count.toLocaleString(locale)}</strong> }} />
                  : t('reglements.title_plain')}
              </p>
              <SearchModeToggle mode={searchMode} onChange={toggleMode} />
            </div>
            <div className="h-px bg-border/60 mb-3" />

            {searchMode === 'guidee' ? (
              <SmartSearchBar context="reglements" onSmartSearch={handleGuidedSearch} searching={searching} />
            ) : (
              <>
                <form onSubmit={handleSubmit} className="flex gap-2">
                  <div className="flex-1 flex items-center gap-3 rounded-xl border border-border bg-background px-4 py-2.5 focus-within:ring-2 focus-within:ring-primary/30 transition-all">
                    <svg className="h-4 w-4 text-muted-foreground shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      value={query}
                      onChange={e => setQuery(e.target.value)}
                      placeholder={t('reglements.placeholder')}
                      className="flex-1 bg-transparent outline-none text-[14px] placeholder:text-muted-foreground/60"
                    />
                    {query && (
                      <button type="button" onClick={handleReset} className="text-muted-foreground hover:text-foreground transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <button type="submit" disabled={searching || !query.trim()}
                    className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-[13px] hover:bg-primary/90 disabled:opacity-50 transition-colors shrink-0">
                    {searching ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : t('articles.search_submit')}
                  </button>
                </form>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {CELEX_FILTERS.map(f => (
                    <button key={f.celex} onClick={() => handleCelexFilter(f.celex)}
                      className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-all ${
                        activeCelex === f.celex ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-background border-border text-muted-foreground hover:border-indigo-400'
                      }`}>
                      {f.labelKey ? t(f.labelKey) : f.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* ── Results ── */}
          <div ref={resultsRef}>
            {searching && (
              <div className="flex flex-col items-center py-24 gap-4 text-muted-foreground">
                <div className="w-8 h-8 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
                <p className="text-sm">{t('articles.semantic_searching')}</p>
              </div>
            )}

            {!searching && searchDone && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    <Trans t={t} i18nKey="articles.results_for" count={allResults.length} components={{
                      strong: <strong className="text-foreground">{allResults.length}</strong>,
                      q: <em className="text-foreground">{t('articles.quoted', { query })}</em>,
                    }} />
                    {searchTime != null && (
                      <span className="ml-2 text-xs text-muted-foreground/60">
                        ({searchTime < 1000 ? `${searchTime} ms` : `${(searchTime / 1000).toFixed(2)} s`})
                      </span>
                    )}
                  </p>
                  <button
                    onClick={handleReset}
                    className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    {t('articles.new_search')}
                  </button>
                </div>

                {results.length === 0 ? (
                  <div className="text-center py-24 text-muted-foreground">
                    <p className="text-5xl mb-4">🔎</p>
                    <p className="font-semibold text-foreground mb-1">{t('articles.no_article_found')}</p>
                    <p className="text-sm">{t('reglements.no_results_hint')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {results.map(r => <RegCard key={r.id} r={r} />)}
                    {displayCount < allResults.length && (
                      <div className="flex justify-center pt-4">
                        <button
                          onClick={loadMore}
                          className="flex items-center gap-2 px-6 py-2.5 rounded-xl border-2 border-border hover:border-primary/40 hover:bg-accent text-sm font-medium transition-all"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                          {t('articles.see_more_remaining', { remaining: allResults.length - displayCount })}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </main>

        <PublicFooter />
      </div>
      <MCPBadge />
    </>
  );
}
